import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faDownload,
  faEye,
  faFileCsv,
  faHistory,
  faRefresh,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  approveBankDetailUpdate,
  checkProviderBankValidationApprover,
  disapproveBankDetailUpdate,
  getBankDetailUpdateHistory,
  getCompletedBankDetailUpdates,
  getPendingBankDetailUpdates,
  regenerateBankValidationFileUrl,
} from "../../services/providerBankValidationApi";

const DATE_OPTIONS = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-PH", DATE_OPTIONS);
};

const titleCase = (value) => {
  const text = String(value || "").trim();
  if (!text) return "-";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatEntityLabel = (row) => {
  const code = String(row?.entity_id || "").trim();
  const name = String(row?.entity_name || "").trim();

  if (code && name) return `[${code}] ${name}`;
  if (name) return name;
  if (code) return code;
  return "-";
};

const statusBadge = (status) => {
  const normalized = String(status || "").toLowerCase();
  const map = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    disapproved: "bg-red-100 text-red-800",
  };

  return (
    <span className={`px-2 py-1 rounded text-xs font-semibold ${map[normalized] || "bg-gray-100 text-gray-700"}`}>
      {titleCase(normalized)}
    </span>
  );
};

const BANK_DETAIL_FIELDS = [
  { key: "bank_acct_name", label: "Acct Name" },
  { key: "bank_acct_no", label: "Acct No" },
  { key: "bank_name", label: "Bank" },
  { key: "bank_branch", label: "Branch" },
];

const normalizeBankComparisonValue = (fieldKey, value) => {
  const rawValue = String(value ?? "").trim();
  if (!rawValue || rawValue === "-") {
    return "";
  }

  if (fieldKey === "bank_acct_no") {
    return rawValue.replace(/[\s-]/g, "");
  }

  if (fieldKey === "bank_name") {
    return rawValue
      .replace(/^\[\s*\d+\s*\]\s*/i, "")
      .replace(/\s+/g, " ")
      .toLowerCase();
  }

  return rawValue.replace(/\s+/g, " ").toLowerCase();
};

const isBankFieldUpdated = (row, fieldKey) => {
  const oldValue = normalizeBankComparisonValue(fieldKey, row[`old_${fieldKey}`]);
  const newValue = normalizeBankComparisonValue(fieldKey, row[`new_${fieldKey}`]);

  if (fieldKey === "bank_name") {
    const oldBankId = String(row.old_bank_id ?? "").trim();
    const newBankId = String(row.new_bank_id ?? "").trim();
    if (oldBankId !== newBankId) {
      return true;
    }
  }

  return oldValue !== newValue;
};

const formatBankFieldValue = (row, prefix, fieldKey) => {
  if (fieldKey !== "bank_name") {
    return row[`${prefix}_${fieldKey}`] || "-";
  }

  const bankId = String(row[`${prefix}_bank_id`] ?? "").trim();
  const bankName = String(row[`${prefix}_bank_name`] ?? "").trim();

  if (bankName) {
    if (bankId && !/^\[\s*\d+\s*\]\s+/i.test(bankName)) {
      return `[${bankId}] ${bankName}`;
    }
    return bankName;
  }

  if (bankId) {
    return `[${bankId}]`;
  }

  return "-";
};

const bankDetails = (row, prefix) => {
  const isNewColumn = prefix === "new";

  return (
    <div className="space-y-1 text-xs leading-5 min-w-[220px]">
      {BANK_DETAIL_FIELDS.map((field) => {
        const isUpdated = isBankFieldUpdated(row, field.key);
        const badgeLabel = isNewColumn ? "Updated" : "Previous";
        const badgeClass = isNewColumn
          ? "bg-emerald-100 text-emerald-700"
          : "bg-amber-100 text-amber-700";
        const highlightClass = isUpdated
          ? isNewColumn
            ? "border-emerald-200 bg-emerald-50"
            : "border-amber-200 bg-amber-50"
          : "border-transparent";

        return (
          <div
            key={`${prefix}-${field.key}`}
            className={`rounded border px-2 py-1 ${highlightClass}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className="font-semibold">{field.label}:</span>{" "}
                <span className="break-words">{formatBankFieldValue(row, prefix, field.key)}</span>
              </div>
              {isUpdated && (
                <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}>
                  {badgeLabel}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const LEGACY_UPLOAD_BASE_URL = "https://allaccess.net.ph/upload_doc";
const LEGACY_UPLOAD_PATH_REGEX = /\/upload_doc\//i;

const isLegacyUploadUrl = (value) => LEGACY_UPLOAD_PATH_REGEX.test(String(value || ""));

const looksLikeLegacyFileName = (value) =>
  /\.[a-z0-9]{2,8}(\?|#|$)/i.test(String(value || "").trim());

const buildLegacyUploadUrl = (folder, rawValue) => {
  const value = String(rawValue || "").trim();
  if (!value) return null;

  if (/^https?:\/\//i.test(value)) {
    return isLegacyUploadUrl(value) ? value : null;
  }

  if (!looksLikeLegacyFileName(value)) {
    return null;
  }

  const normalizedFolder = String(folder || "")
    .trim()
    .replace(/^\/+|\/+$/g, "");
  if (!normalizedFolder) {
    return null;
  }

  let normalizedFileName = value;
  try {
    normalizedFileName = decodeURIComponent(value);
  } catch (error) {
    normalizedFileName = value;
  }

  return `${LEGACY_UPLOAD_BASE_URL}/${encodeURIComponent(normalizedFolder)}/${encodeURIComponent(normalizedFileName)}`;
};

const resolveVersionedFileLinks = ({ folder, rawValue, resolvedUrl }) => {
  const normalizedResolvedUrl = String(resolvedUrl || "").trim();
  const legacyUrlFromResolved = isLegacyUploadUrl(normalizedResolvedUrl)
    ? normalizedResolvedUrl
    : null;
  const legacyUrl = legacyUrlFromResolved || buildLegacyUploadUrl(folder, rawValue);
  const version2Url =
    normalizedResolvedUrl && !isLegacyUploadUrl(normalizedResolvedUrl)
      ? normalizedResolvedUrl
      : null;

  return {
    version1Url: legacyUrl,
    version2Url,
  };
};

const canRegenerateUploadId = (uploadId) => {
  const value = String(uploadId || "").trim();
  if (!value) return false;
  if (/^https?:\/\//i.test(value)) return false;
  if (looksLikeLegacyFileName(value)) return false;
  return true;
};

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const ProviderBankValidationPage = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [pendingRows, setPendingRows] = useState([]);
  const [completedRows, setCompletedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTitle, setHistoryTitle] = useState("");
  const [selectedDocuments, setSelectedDocuments] = useState(null);
  const [regeneratingDocumentKey, setRegeneratingDocumentKey] = useState("");

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const approverResponse = await checkProviderBankValidationApprover();
      const isApprover = Number(approverResponse.data?.data?.is_approver || 0) === 1;
      setAccessDenied(!isApprover);
      if (!isApprover) return;

      const [pendingResponse, completedResponse] = await Promise.all([
        getPendingBankDetailUpdates(),
        getCompletedBankDetailUpdates(),
      ]);

      setPendingRows(pendingResponse.data?.data || []);
      setCompletedRows(completedResponse.data?.data || []);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error(error.response?.data?.message || "Failed to load bank validation requests.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const currentRows = activeTab === "pending" ? pendingRows : completedRows;

  const openDocumentsModal = (row) => {
    const detailsLinks = resolveVersionedFileLinks({
      folder: row.bank_details_folder || "mef",
      rawValue: row.bank_details_file,
      resolvedUrl: row.bank_details_file_url,
    });
    const proofLinks = resolveVersionedFileLinks({
      folder: "bank_statement",
      rawValue: row.bank_statement_file,
      resolvedUrl: row.bank_statement_file_url,
    });

    const entries = [
      {
        key: "details",
        label: row.bank_details_label || "Bank Details File",
        fileName: row.bank_details_file_name || row.bank_details_file || "",
        entityType: row.entity_type,
        fileKind: "bank_details",
        uploadId: row.bank_details_file || "",
        expiresAt: null,
        ...detailsLinks,
      },
      {
        key: "proof",
        label: "Bank Proof",
        fileName: row.bank_statement_file_name || row.bank_statement_file || "",
        entityType: row.entity_type,
        fileKind: "bank_proof",
        uploadId: row.bank_statement_file || "",
        expiresAt: null,
        ...proofLinks,
      },
    ];

    const hasAtLeastOneFile = entries.some(
      (entry) => !!(entry.fileName || entry.version1Url || entry.version2Url)
    );
    if (!hasAtLeastOneFile) {
      toast.warning("No file available.");
      return;
    }

    setSelectedDocuments({
      title: `${titleCase(row.entity_type)} ${formatEntityLabel(row)}`,
      entries,
    });
  };

  const handleRegenerateDocumentUrl = async (documentEntry) => {
    const uploadId = String(documentEntry?.uploadId || "").trim();
    if (!uploadId) {
      toast.error("No upload reference found for this file.");
      return;
    }

    if (!canRegenerateUploadId(uploadId)) {
      toast.warning("This file is from version1.0 and cannot regenerate a version2.0 URL.");
      return;
    }

    const regenerateKey = `${documentEntry.key}:${uploadId}`;
    setRegeneratingDocumentKey(regenerateKey);
    try {
      const response = await regenerateBankValidationFileUrl({
        entity_type: documentEntry.entityType,
        file_kind: documentEntry.fileKind,
        upload_id: uploadId,
      });

      const signedUrl = response?.data?.signed_url || "";
      const expiresAt = response?.data?.expires_at || "";

      if (!signedUrl) {
        toast.error("Failed to regenerate URL.");
        return;
      }

      setSelectedDocuments((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          entries: prev.entries.map((entry) =>
            entry.key === documentEntry.key
              ? {
                  ...entry,
                  version2Url: signedUrl,
                  expiresAt: expiresAt || entry.expiresAt || null,
                }
              : entry
          ),
        };
      });

      toast.success(response?.data?.message || "Pre-signed URL regenerated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to regenerate URL.");
    } finally {
      setRegeneratingDocumentKey("");
    }
  };

  const loadHistory = async (row) => {
    try {
      const response = await getBankDetailUpdateHistory({
        entity_type: row.entity_type,
        entity_id: row.entity_id,
      });
      setHistoryRows(response.data?.data || []);
      setHistoryTitle(`${titleCase(row.entity_type)} ${formatEntityLabel(row)}`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load bank validation history.");
    }
  };

  const handleApprove = async (row) => {
    const result = await Swal.fire({
      title: "Approve bank details?",
      text: `${titleCase(row.entity_type)} ${formatEntityLabel(row)}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: "#16a34a",
    });

    if (!result.isConfirmed) return;

    try {
      await approveBankDetailUpdate(row.tbl_id);
      toast.success("Bank detail update approved.");
      await loadRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to approve request.");
    }
  };

  const handleDisapprove = async (row) => {
    const result = await Swal.fire({
      title: "Disapprove bank details",
      input: "textarea",
      inputPlaceholder: "Enter rejection reason",
      inputAttributes: { maxlength: 2000 },
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Disapprove",
      confirmButtonColor: "#dc2626",
      inputValidator: (value) => (!value || !value.trim() ? "Rejection reason is required." : undefined),
    });

    if (!result.isConfirmed) return;

    try {
      await disapproveBankDetailUpdate(row.tbl_id, result.value.trim());
      toast.success("Bank detail update disapproved.");
      await loadRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to disapprove request.");
    }
  };

  const exportRows = () => {
    const columns = [
      ["Type", "entity_type"],
      ["Code", "entity_id"],
      ["Name", "entity_name"],
      ["Old Account Name", "old_bank_acct_name"],
      ["Old Account No", "old_bank_acct_no"],
      ["Old Bank", "old_bank_name"],
      ["Old Branch", "old_bank_branch"],
      ["New Account Name", "new_bank_acct_name"],
      ["New Account No", "new_bank_acct_no"],
      ["New Bank", "new_bank_name"],
      ["New Branch", "new_bank_branch"],
      ["Encoded Date", "encoded_at"],
      ["Encoded By", "encoded_by_name"],
      ["Validated Date", "validated_at"],
      ["Validated By", "validated_by_name"],
      ["Rejection Reason", "disapproval_reason"],
      ["Status", "status"],
    ];

    const csv = [
      columns.map(([label]) => csvEscape(label)).join(","),
      ...currentRows.map((row) => columns.map(([, key]) => csvEscape(row[key])).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `provider-bank-validation-${activeTab}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const summary = useMemo(() => ({
    pending: pendingRows.length,
    completed: completedRows.length,
  }), [pendingRows.length, completedRows.length]);

  const renderFileButton = (row, kind, label) => {
    const fileName = kind === "details"
      ? row.bank_details_file_name || row.bank_details_file
      : row.bank_statement_file_name || row.bank_statement_file;
    const fileUrl = kind === "details" ? row.bank_details_file_url : row.bank_statement_file_url;

    if (!fileName && !fileUrl) return <span className="text-xs text-gray-400">No file</span>;

    return (
      <button
        type="button"
        onClick={() => openDocumentsModal(row)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded"
        title={fileName || label}
      >
        <FontAwesomeIcon icon={faEye} />
        <span className="max-w-[160px] truncate">{fileName || label}</span>
      </button>
    );
  };

  const renderRows = (rows, completed = false) => {
    if (loading) {
      return (
        <tr>
          <td colSpan={completed ? 13 : 10} className="px-4 py-8 text-center text-sm text-gray-500">Loading requests...</td>
        </tr>
      );
    }

    if (!rows.length) {
      return (
        <tr>
          <td colSpan={completed ? 13 : 10} className="px-4 py-8 text-center text-sm text-gray-500">No records found.</td>
        </tr>
      );
    }

    return rows.map((row) => (
      <tr key={row.tbl_id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
        <td className="px-3 py-3 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            {!completed && (
              <>
                <button
                  type="button"
                  onClick={() => handleApprove(row)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded bg-emerald-600 hover:bg-emerald-700 text-white"
                  title="Approve"
                >
                  <FontAwesomeIcon icon={faCheck} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDisapprove(row)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded bg-red-600 hover:bg-red-700 text-white"
                  title="Disapprove"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => loadHistory(row)}
              className="h-8 w-8 inline-flex items-center justify-center rounded bg-gray-700 hover:bg-gray-800 text-white"
              title="View History"
            >
              <FontAwesomeIcon icon={faHistory} />
            </button>
          </div>
        </td>
        <td className="px-3 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap">{titleCase(row.entity_type)}</td>
        <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{row.entity_id}</td>
        <td className="px-3 py-3 text-sm text-gray-700 min-w-[220px]">{row.entity_name || "-"}</td>
        <td className="px-3 py-3">{bankDetails(row, "old")}</td>
        <td className="px-3 py-3">{bankDetails(row, "new")}</td>
        <td className="px-3 py-3">{renderFileButton(row, "details", row.bank_details_label)}</td>
        <td className="px-3 py-3">{renderFileButton(row, "proof", "Bank Proof")}</td>
        <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{formatDate(row.encoded_at)}</td>
        <td className="px-3 py-3 text-xs text-gray-700 min-w-[160px]">{row.encoded_by_name || "-"}</td>
        {completed && (
          <>
            <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{formatDate(row.validated_at)}</td>
            <td className="px-3 py-3 text-xs text-gray-700 min-w-[160px]">{row.validated_by_name || "-"}</td>
            <td className="px-3 py-3 text-xs text-gray-700 min-w-[220px]">{row.disapproval_reason || "-"}</td>
          </>
        )}
        <td className="px-3 py-3 whitespace-nowrap">{statusBadge(row.status)}</td>
      </tr>
    ));
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <ToastContainer position="top-right" />
      <div className="max-w-full mx-auto">
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-1">Provider / Physician Bank Validation</h1>
            <p className="text-gray-600">Review pending bank detail changes before they update provider or physician records.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadRows}
              disabled={loading}
              className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded flex items-center gap-2 text-sm"
            >
              <FontAwesomeIcon icon={faRefresh} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              type="button"
              onClick={exportRows}
              disabled={!currentRows.length}
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded flex items-center gap-2 text-sm"
            >
              <FontAwesomeIcon icon={faFileCsv} />
              CSV
            </button>
          </div>
        </div>

        {accessDenied ? (
          <div className="bg-white border border-red-200 rounded p-6 text-red-700">
            You are not authorized to validate provider or physician bank updates.
          </div>
        ) : (
          <>
            <div className="bg-white rounded shadow-sm mb-4">
              <div className="flex border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => setActiveTab("pending")}
                  className={`px-4 py-3 text-sm font-semibold border-b-2 ${activeTab === "pending" ? "border-blue-600 text-blue-700" : "border-transparent text-gray-600 hover:text-gray-800"}`}
                >
                  Pending ({summary.pending})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("completed")}
                  className={`px-4 py-3 text-sm font-semibold border-b-2 ${activeTab === "completed" ? "border-blue-600 text-blue-700" : "border-transparent text-gray-600 hover:text-gray-800"}`}
                >
                  Approved / Rejected ({summary.completed})
                </button>
              </div>
            </div>

            <div className="bg-white rounded shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                    <tr>
                      <th className="px-3 py-3">Actions</th>
                      <th className="px-3 py-3">Type</th>
                      <th className="px-3 py-3">Code</th>
                      <th className="px-3 py-3">Name</th>
                      <th className="px-3 py-3">{activeTab === "pending" ? "Current Bank Details" : "Old Bank Details"}</th>
                      <th className="px-3 py-3">{activeTab === "pending" ? "Requested Bank Details" : "New Bank Details"}</th>
                      <th className="px-3 py-3">Bank Details File</th>
                      <th className="px-3 py-3">Bank Proof</th>
                      <th className="px-3 py-3">Encoded Date</th>
                      <th className="px-3 py-3">Encoded By</th>
                      {activeTab === "completed" && (
                        <>
                          <th className="px-3 py-3">Validated Date</th>
                          <th className="px-3 py-3">Validated By</th>
                          <th className="px-3 py-3">Rejection Reason</th>
                        </>
                      )}
                      <th className="px-3 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows(currentRows, activeTab === "completed")}</tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {historyTitle && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-xl w-full max-w-6xl max-h-[85vh] overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Bank Validation History - {historyTitle}</h2>
              <button type="button" onClick={() => setHistoryTitle("")} className="h-8 w-8 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="overflow-auto max-h-[70vh]">
              <table className="min-w-full text-left">
                <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-3">Action</th>
                    <th className="px-3 py-3">Actor</th>
                    <th className="px-3 py-3">Remarks</th>
                    <th className="px-3 py-3">Old Bank Details</th>
                    <th className="px-3 py-3">New Bank Details</th>
                    <th className="px-3 py-3">Bank Details File</th>
                    <th className="px-3 py-3">Bank Proof</th>
                    <th className="px-3 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.length ? historyRows.map((row) => (
                    <tr key={row.tbl_id} className="border-b align-top">
                      <td className="px-3 py-3">{statusBadge(row.action)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 min-w-[160px]">{row.actor_name || "-"}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 min-w-[220px]">{row.remarks || "-"}</td>
                      <td className="px-3 py-3">{bankDetails(row, "old")}</td>
                      <td className="px-3 py-3">{bankDetails(row, "new")}</td>
                      <td className="px-3 py-3">{renderFileButton(row, "details", row.bank_details_label)}</td>
                      <td className="px-3 py-3">{renderFileButton(row, "proof", "Bank Proof")}</td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">{formatDate(row.action_at)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-sm text-gray-500">No history found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {selectedDocuments && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">
                Provider Documents - {selectedDocuments.title}
              </h2>
              <button
                type="button"
                onClick={() => setSelectedDocuments(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto max-h-[70vh]">
              {selectedDocuments.entries.map((documentEntry) => {
                const hasVersion1 = !!documentEntry.version1Url;
                const hasVersion2 = !!documentEntry.version2Url;
                const hasAnyVersion = hasVersion1 || hasVersion2;
                const canRegenerate = canRegenerateUploadId(documentEntry.uploadId);
                const regenerateKey = `${documentEntry.key}:${String(documentEntry.uploadId || "").trim()}`;
                const isRegenerating = regeneratingDocumentKey === regenerateKey;
                const badgeClass = !hasAnyVersion
                  ? "bg-gray-100 text-gray-600"
                  : hasVersion1 && hasVersion2
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-blue-100 text-blue-700";
                const badgeText = !hasAnyVersion
                  ? "No File"
                  : hasVersion1 && hasVersion2
                  ? "V1 + V2"
                  : hasVersion2
                  ? "V2 Available"
                  : "V1 Available";

                return (
                  <div
                    key={documentEntry.key}
                    className="rounded-lg border border-slate-200 bg-slate-50/60 p-4"
                  >
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <h3 className="text-sm font-semibold text-slate-900">
                        {documentEntry.label}
                      </h3>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}
                      >
                        {badgeText}
                      </span>
                    </div>
                    <p className="text-xs text-gray-700 break-all mb-2">
                      {documentEntry.fileName || "No uploaded file available."}
                    </p>
                    {documentEntry.expiresAt && (
                      <p className="text-[11px] text-gray-500 mb-2">
                        URL expires at: {documentEntry.expiresAt}
                      </p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href={documentEntry.version2Url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center space-x-1 px-3 py-1.5 rounded-md text-xs ${
                          documentEntry.version2Url
                            ? "bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-gray-200 text-gray-500 pointer-events-none"
                        }`}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        <span>View V2.0</span>
                      </a>
                      <a
                        href={documentEntry.version1Url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center space-x-1 px-3 py-1.5 rounded-md text-xs ${
                          documentEntry.version1Url
                            ? "bg-indigo-600 text-white hover:bg-indigo-700"
                            : "bg-gray-200 text-gray-500 pointer-events-none"
                        }`}
                      >
                        <FontAwesomeIcon icon={faEye} />
                        <span>View V1.0</span>
                      </a>
                      <button
                        type="button"
                        onClick={() => handleRegenerateDocumentUrl(documentEntry)}
                        className="inline-flex items-center space-x-1 px-3 py-1.5 rounded-md text-xs bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-50"
                        disabled={!canRegenerate || isRegenerating}
                      >
                        <FontAwesomeIcon icon={faRefresh} className={isRegenerating ? "animate-spin" : ""} />
                        <span>{isRegenerating ? "Regenerating..." : "Regenerate URL"}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderBankValidationPage;
